// src/features/channels/ChannelsPage.tsx
import { useState, useEffect, type FormEvent } from 'react';
import { useGuild } from '../../lib/GuildContext';
import { formatGuildName } from '../../lib/formatGuildName';
import { useUpdateGuildSettings, type ChannelSettings } from './useGuildSettings';
import { ChannelPicker } from '../../components/pickers/ChannelPicker';
import { useCachedChannels } from '../../components/pickers/useGuildStructure';
import './ChannelsPage.css';

const SNOWFLAKE_RE = /^\d{17,20}$/;

function WelcomeLeaveCard({ guild }: { guild: { settings: ChannelSettings } }) {
  const { selectedGuildId } = useGuild();
  const update = useUpdateGuildSettings(selectedGuildId);

  const [welcomeChannelId, setWelcomeChannelId] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [leaveMessage, setLeaveMessage] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setWelcomeChannelId(guild.settings.welcomeChannelId ?? '');
    setWelcomeMessage(guild.settings.welcomeMessage ?? '');
    setLeaveMessage(guild.settings.leaveMessage ?? '');
  }, [guild]);

  const welcomeIdValid = welcomeChannelId === '' || SNOWFLAKE_RE.test(welcomeChannelId);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!welcomeIdValid) return;

    update.mutate(
      {
        welcomeChannelId: welcomeChannelId || null,
        welcomeMessage: welcomeMessage || '',
        leaveMessage: leaveMessage || '',
      },
      {
        onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2500); },
      },
    );
  }

  return (
    <article className="card" style={{ marginBottom: '1.25rem' }}>
      <form onSubmit={handleSubmit}>
        <header className="card__header channels-card__header--with-action">
          <div>
            <p className="card__eyebrow">Member events</p>
            <h2 className="card__title">Welcome / leave</h2>
          </div>
          <div className="channels-card__header-action">
            <button className="button-primary" type="submit" disabled={update.isPending || !welcomeIdValid}>
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </header>
        <div className="card__content channels-stack">
          <div className="channels-field">
            <label htmlFor="welcomeChannelId">Channel ID</label>
            <ChannelPicker
              guildId={selectedGuildId}
              value={welcomeChannelId}
              onChange={setWelcomeChannelId}
              placeholder="e.g. 123456789012345678"
            />
            {!welcomeIdValid && <p className="channels-field__error">Must be a Discord snowflake — 17 to 20 digits.</p>}
          </div>
          <div className="channels-field">
            <label htmlFor="welcomeMessage">Welcome message</label>
            <textarea
              id="welcomeMessage"
              className="input"
              rows={2}
              maxLength={500}
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              placeholder="Use {user}, {server}, {count} as placeholders"
            />
          </div>
          <div className="channels-field">
            <label htmlFor="leaveMessage">Leave message</label>
            <textarea
              id="leaveMessage"
              className="input"
              rows={2}
              maxLength={500}
              value={leaveMessage}
              onChange={(e) => setLeaveMessage(e.target.value)}
              placeholder="Use {user}, {server}, {count} as placeholders"
            />
          </div>
          {saved && <span style={{ color: 'var(--positive)', fontSize: 13 }}>Saved</span>}
          {update.isError && <span style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t save. Try again.</span>}
        </div>
      </form>
    </article>
  );
}

function LoggingCard({ guild }: { guild: { settings: ChannelSettings } }) {
  const { selectedGuildId } = useGuild();
  const update = useUpdateGuildSettings(selectedGuildId);
  const [logChannelId, setLogChannelId] = useState('');

  useEffect(() => {
    setLogChannelId(guild.settings.logChannelId ?? '');
  }, [guild]);

  const currentSaved = guild.settings.logChannelId ?? '';
  const logIdValid = logChannelId === '' || SNOWFLAKE_RE.test(logChannelId);

  function apply() {
    if (!SNOWFLAKE_RE.test(logChannelId)) return;
    update.mutate({ logChannelId });
  }

  function remove() {
    setLogChannelId('');
    update.mutate({ logChannelId: null });
  }

  return (
    <article className="card">
      <header className="card__header">
        <div>
          <p className="card__eyebrow">Reaction roles</p>
          <h2 className="card__title">Reaction Role Log Channel</h2>
        </div>
      </header>
      <div className="card__content">
        <p className="card__hint">
          Logs reaction-role activity only (menus created, roles added or removed) — not general
          command usage, moderation actions, or anything else.
        </p>
        <div className="channels-field">
          <label htmlFor="logChannelId">Channel ID</label>
          <div className="channels-inline-action">
            <div style={{ flex: 1 }}>
              <ChannelPicker
                guildId={selectedGuildId}
                value={logChannelId}
                onChange={setLogChannelId}
                placeholder="e.g. 123456789012345678"
              />
            </div>
            <button type="button" className="button-secondary" onClick={apply} disabled={!logChannelId || !logIdValid || update.isPending}>
              Apply
            </button>
            <button type="button" className="button-danger" onClick={remove} disabled={!currentSaved || update.isPending}>
              Remove
            </button>
          </div>
          {!logIdValid && <p className="channels-field__error">Must be a Discord snowflake — 17 to 20 digits.</p>}
          {update.isError && <p className="channels-field__error">Couldn&apos;t save. Try again.</p>}
        </div>
      </div>
    </article>
  );
}

function BlacklistCard({ guild }: { guild: { settings: ChannelSettings } }) {
  const { selectedGuildId } = useGuild();
  const update = useUpdateGuildSettings(selectedGuildId);
  const { data: cachedChannels } = useCachedChannels(selectedGuildId);
  const [blacklisted, setBlacklisted] = useState<string[]>([]);
  const [newBlacklistId, setNewBlacklistId] = useState('');

  useEffect(() => {
    setBlacklisted(guild.settings.blacklistedChannels ?? []);
  }, [guild]);

  const newBlacklistValid = newBlacklistId === '' || SNOWFLAKE_RE.test(newBlacklistId);

  function addBlacklisted() {
    if (!SNOWFLAKE_RE.test(newBlacklistId) || blacklisted.includes(newBlacklistId) || blacklisted.length >= 50) return;
    const next = [...blacklisted, newBlacklistId];
    setBlacklisted(next);
    setNewBlacklistId('');
    update.mutate({ blacklistedChannels: next });
  }

  function removeBlacklisted(id: string) {
    const next = blacklisted.filter((c) => c !== id);
    setBlacklisted(next);
    update.mutate({ blacklistedChannels: next });
  }

  return (
    <article className="card">
      <header className="card__header">
        <div>
          <p className="card__eyebrow">{blacklisted.length} / 50</p>
          <h2 className="card__title">Blacklisted channels</h2>
        </div>
      </header>
      <p className="card__hint">Commands are silently ignored in these channels.</p>
      <div className="channels-blacklist-add">
        <div style={{ flex: 1 }}>
          <ChannelPicker
            guildId={selectedGuildId}
            value={newBlacklistId}
            onChange={setNewBlacklistId}
            placeholder="Channel ID to add"
          />
        </div>
        <button
          type="button"
          className="button-secondary"
          onClick={addBlacklisted}
          disabled={!newBlacklistId || !newBlacklistValid || blacklisted.length >= 50 || update.isPending}
        >
          Add
        </button>
      </div>
      {!newBlacklistValid && newBlacklistId && (
        <p className="channels-field__error">Must be a Discord snowflake — 17 to 20 digits.</p>
      )}
      <div className="capped-scroll">
        {blacklisted.map((id) => {
          const cached = cachedChannels?.find((c) => c.channel_id === id);
          return (
            <div className="cmd-row" key={id}>
              <span className="channels-blacklist-entry">
                <span className="channels-blacklist-entry__id">{id}</span>
                {cached && <span className="channels-blacklist-entry__name">{cached.channel_name}</span>}
              </span>
              <button type="button" className="button-danger" onClick={() => removeBlacklisted(id)} disabled={update.isPending}>
                Remove
              </button>
            </div>
          );
        })}
      </div>
      {update.isError && <p className="card__error">Couldn&apos;t save. Try again.</p>}
    </article>
  );
}

export function ChannelsPage() {
  const { guilds, selectedGuildId } = useGuild();
  const guild = guilds.find((g) => g.guild_id === selectedGuildId);

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">{guild ? formatGuildName(guild.guild_name, guild.guild_id) : 'No server selected'}</p>
        <h1 className="page-header__title">Channels</h1>
      </div>

      {guild && (
        <>
          <WelcomeLeaveCard guild={{ settings: guild.settings as ChannelSettings }} />
          <div className="channels-columns">
            <LoggingCard guild={{ settings: guild.settings as ChannelSettings }} />
            <BlacklistCard guild={{ settings: guild.settings as ChannelSettings }} />
          </div>
        </>
      )}
    </>
  );
}
